import fehaSnapshotTamperDemo from './fixtures/asenion.fehaads.runtime-monitoring.status.tamper-demo.json';
import owaspSnapshotTamperDemo from './fixtures/asenion.owaspagentic.runtime-monitoring.status.tamper-demo.json';
import type {
  MonitoringControlRecord,
  MonitoringStatus,
} from './monitoring-types';

// Tamper-demo registry: dev/demo-only — never auto-selected; only the detail
// page activates it when the URL query param `?monitoring-tamper=1` is present.
//
// The normal-mode registry that used to live here has been removed (Phase 0
// G1 client rewire, F6.2): real policy data now comes exclusively from the
// live `guardianControlSurface` query (see
// monitoring-control-surface-adapter.ts + monitoring-detail-page.tsx /
// monitoring-tab.tsx). On a query error or an empty result the surface
// renders an honest empty/error state — it never falls back to a fixture.
const TAMPER_DEMO_REGISTRY: Record<string, MonitoringStatus> = {
  'asenion.fehaads.runtime-monitoring':
    fehaSnapshotTamperDemo as unknown as MonitoringStatus,
  'asenion.owaspagentic.runtime-monitoring':
    owaspSnapshotTamperDemo as unknown as MonitoringStatus,
};

type ProviderResult =
  | { data: MonitoringStatus; error: null }
  | { data: null; error: 'missing-snapshot' };

/**
 * Returns the tamper-demo monitoring status snapshot for a given policy id,
 * or null. Dev/demo-only, reachable only via `?monitoring-tamper=1` on the
 * detail page — this is NEVER the normal data path (that's the live
 * `guardianControlSurface` query); deliberately named to make that
 * impossible to confuse at a call site.
 */
export function getTamperDemoMonitoringStatus(
  policyId: string
): ProviderResult {
  const snapshot = TAMPER_DEMO_REGISTRY[policyId];
  if (!snapshot) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[monitoring] missing-snapshot: no tamper-demo status snapshot registered for policyId="${policyId}"`
      );
    }
    return { data: null, error: 'missing-snapshot' };
  }
  return { data: snapshot, error: null };
}

/**
 * Validates the join between the snapshot and the assessment's control list.
 * Dev-only: logs warnings on any mismatch; never throws in production.
 */
export function validateMonitoringJoin(
  snapshot: MonitoringStatus,
  assessmentControlIds: string[]
): boolean {
  if (process.env.NODE_ENV === 'production') {
    return true;
  }

  let valid = true;
  const snapshotIds = new Set(snapshot.controls.map((c) => c.controlId));
  const assessmentSet = new Set(assessmentControlIds);

  // Duplicate controlIds in snapshot
  if (snapshotIds.size !== snapshot.controls.length) {
    console.warn(
      '[monitoring] bad-join: duplicate controlId entries in snapshot'
    );
    valid = false;
  }

  // Every snapshot controlId must be in the assessment
  for (const id of snapshotIds) {
    if (assessmentSet.size > 0 && !assessmentSet.has(id)) {
      console.warn(
        `[monitoring] bad-join: snapshot controlId "${id}" not found in assessment controls`
      );
      valid = false;
    }
  }

  // Every assessment control must have a snapshot record
  for (const id of assessmentSet) {
    if (!snapshotIds.has(id)) {
      console.warn(
        `[monitoring] bad-join: assessment controlId "${id}" has no snapshot record`
      );
      valid = false;
    }
  }

  // summary cross-check (recompute and compare)
  const records = snapshot.controls;
  const computedActive = records.filter((c) => c.activity === 'active').length;
  const computedIdle = records.filter((c) => c.activity === 'idle').length;
  const computedAttested = records.filter(
    (c) => c.attestation === 'attested'
  ).length;
  // Note: summary.totalConsults = policy-level consult events (NOT the sum of
  // per-control consults, which is summary.controlEvaluations). Do not cross-check
  // the two — they measure different things.
  const computedEvaluations = records.reduce((n, c) => n + c.consults, 0);

  if (snapshot.summary.totalControls !== records.length) {
    console.warn(
      `[monitoring] bad-join: summary.totalControls=${snapshot.summary.totalControls} but controls.length=${records.length}`
    );
    valid = false;
  }
  if (snapshot.summary.active !== computedActive) {
    console.warn(
      `[monitoring] bad-join: summary.active=${snapshot.summary.active} but computed=${computedActive}`
    );
    valid = false;
  }
  if (snapshot.summary.idle !== computedIdle) {
    console.warn(
      `[monitoring] bad-join: summary.idle=${snapshot.summary.idle} but computed=${computedIdle}`
    );
    valid = false;
  }
  if (snapshot.summary.attested !== computedAttested) {
    console.warn(
      `[monitoring] bad-join: summary.attested=${snapshot.summary.attested} but computed=${computedAttested}`
    );
    valid = false;
  }
  if (
    snapshot.summary.controlEvaluations !== undefined &&
    snapshot.summary.controlEvaluations !== computedEvaluations
  ) {
    console.warn(
      `[monitoring] bad-join: summary.controlEvaluations=${snapshot.summary.controlEvaluations} but computed=${computedEvaluations}`
    );
    valid = false;
  }

  return valid;
}

/** Computes attestationGap from records (not summary arithmetic). Prefer summary.attestationGaps when present. */
export function computeAttestationGap(
  controls: MonitoringControlRecord[]
): number {
  return controls.filter(
    (c) => c.activity === 'active' && c.attestation === 'unverified'
  ).length;
}

export const GUIDANCE_LABELS: Record<string, string> = {
  inform: 'Inform',
  warn: 'Warn',
  escalate: 'Escalate',
  decline: 'Decline',
  require_human_review: 'Human review',
};

/**
 * Returns the mapped, user-facing statusSentence for a control record.
 * NEVER falls back to raw statusLine — uses a neutral generated sentence instead.
 */
export function getStatusSentence(record: MonitoringControlRecord): string {
  if (record.statusSentence) {
    return record.statusSentence;
  }
  // Safe fallback: never render raw statusLine
  if (record.activity === 'idle') {
    return 'This control is loaded and available; no agent decisions have matched its triggers in the current window.';
  }
  if (record.attestation === 'unverified') {
    return 'Runtime guidance was issued for this control, but one or more witness records did not verify against the chain — a record-integrity signal, not a statement about the agent.';
  }
  return 'Runtime guidance activity is available for this control.';
}

/** Formats outcomeKinds as a sorted " · " joined string, e.g. "Human review ×25 · Decline ×3". */
export function formatOutcomeDistribution(
  outcomeKinds: Record<string, number>
): string {
  const entries = Object.entries(outcomeKinds)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);
  if (entries.length === 0) {
    return '—';
  }
  return entries
    .map(([kind, count]) => `${GUIDANCE_LABELS[kind] ?? kind} ×${count}`)
    .join(' · ');
}

/**
 * G3 — the witness drawer's "Raw witness records" section state.
 *
 * The drawer checks `org:view_governance` — the SAME capability that gates
 * Monitoring itself (the formerly-separate `org:view_guardian` read
 * capability was retired; see migration 016, which folded guardian reads
 * into `org:view_governance`). Re-checking it here is DEFENSIVE, not
 * something a normal caller hits — a caller who reached this drawer already
 * holds it — but it must still degrade honestly rather than let a denied
 * `guardianWitnessEvents` call surface as a raw GraphQL error in the drawer.
 * Exactly one of three states, and the caller (monitoring-witness-drawer.tsx)
 * must never query while `loading`/`denied` — the whole point is that the
 * query never fires (and so never 403s) when the capability isn't held:
 *   - 'loading' — the capability check hasn't resolved yet; render nothing.
 *   - 'denied'  — capability resolved false; render a quiet note, no query.
 *   - 'visible' — capability resolved true; render the live record list.
 */
export type RawRecordsSectionState = 'loading' | 'denied' | 'visible';

export function getRawRecordsSectionState(capability: {
  loading: boolean;
  allowed: boolean;
}): RawRecordsSectionState {
  if (capability.loading) {
    return 'loading';
  }
  return capability.allowed ? 'visible' : 'denied';
}

/**
 * Guidance filter options with sanitized DOM values.
 * DOM value + label are user-safe; kind maps back to the raw GuidanceKind for filtering.
 */
export const GUIDANCE_FILTER_OPTIONS: {
  value: string;
  label: string;
  kind: string;
}[] = [
  {
    value: 'human-review',
    label: 'Human review',
    kind: 'require_human_review',
  },
  { value: 'decline', label: 'Decline', kind: 'decline' },
  { value: 'escalate', label: 'Escalate', kind: 'escalate' },
  { value: 'warn', label: 'Warn', kind: 'warn' },
  { value: 'inform', label: 'Inform', kind: 'inform' },
];
